import { Hospital, Phone, Shield, Star } from 'lucide-react'
import { data, href, isRouteErrorResponse, useLoaderData, useNavigate } from 'react-router'
import { fromPromise, withRetries } from 'true-myth/task'
import { exponential, jitter } from 'true-myth/task/delay'
import { extractErrorMessage } from '../api/extract-error-message'
import { providersClient } from '../api/providers-client'
import { Avatar } from '../components/avatar'
import { DollarRating } from '../components/dollar-rating'
import { formatPhoneNumber, formatTelHref } from '../components/phone'
import { BackHomeLink, StatusScreen, TryAgainButton } from '../components/status-screen'
import { logger } from '../instrumentation.server'
import { formatProviderSlug, parseProviderNpi } from '../slug'
import type { Route } from './+types/provider'

// Forward any loader-set headers onto the outgoing response. Cache-Control is
// set only by the loader's success return, so a thrown 404/500 never picks it
// up and stays uncached at the edge — this route exports its own ErrorBoundary,
// and React Router runs the boundary route's `headers` fn, so a flat headers
// export would stamp s-maxage onto error responses. Mirrors results.tsx.
export const headers: Route.HeadersFunction = ({ loaderHeaders }) => loaderHeaders

const MAX_RETRIES = 3

// Shared-cache the provider page (and its `.data` sibling) at the vit.al edge.
// The honor-origin cache policy in sst.config.ts has defaultTtl 0, so without
// this header every request reaches the SSR Lambda — and this route is the one
// that pays most for it: the render is ~45 ms, but the un-cached CF→us-west-2
// round trip is 250–550 ms, plus ~1.2 s whenever the low-traffic Lambda is cold
// (VIT-17795).
//
// s-maxage=1209600 is 14 days, set to track upstream freshness rather than the
// page's apparent liveness: the Ribbon half (ratings, cost, educations,
// certifications) refreshes on a 30-day cycle (RIBBON_CACHE_MAX_AGE in
// apps/providers), so 14 days sits inside it. The NPPES half refreshes daily,
// which is the trade-off on record — a name, address or phone could in
// principle be up to 14 days stale. In practice every production deploy
// invalidates `/us*` (sst.config.ts), so the effective TTL is hours.
//
// max-age=1800 is 30 minutes in the browser, half of results.tsx's hour. It has
// to be non-zero for the provider-card prefetch to buy anything (VIT-17906):
// result cards render `prefetch="viewport"`, so the browser fetches this route's
// `.data` while the card is on screen — but under max-age=0 that copy is stale
// the moment it lands, and clicking through revalidates it over the network
// anyway. The prefetch then costs a request and saves nothing.
//
// The trade-off this reverses (VIT-17795): a returning viewer's own cache can
// now mask a deploy invalidation, but only for 30 minutes, and the page is
// derived purely from the NPI in the path.
//
// Set on the loader's success return only, so a thrown 404/500 never picks it
// up and stays uncached at the edge (see the `headers` export above).
const CACHE_CONTROL = 'public, max-age=1800, s-maxage=1209600, stale-while-revalidate=86400'

// A degraded 200 is a real page, but assembled off fewer sources than it should
// have been — the Ribbon block might be missing entirely. Holding that at the
// edge for 14 days would outlast the upstream failure that caused it by weeks,
// so the thin render gets a 5-minute TTL instead: long enough to still shield
// the origin from a hot page, short enough to pick the missing source back up
// once it recovers. No stale-while-revalidate, for the same reason.
//
// The browser still gets a minute, so a card prefetched a moment ago is usable
// on the click that follows — the gap there is seconds. Deliberately far short
// of the 30 minutes a healthy page gets, so a thin render is not held locally
// for anything like as long as the upstream failure that caused it.
const DEGRADED_CACHE_CONTROL = 'public, max-age=60, s-maxage=300'

export const isNonEmpty = (value: string | null | undefined): value is string => value != null && value.trim() !== ''

export const loader = async ({ params }: Route.LoaderArgs) => {
  // The segment is `npi-<npi>-<slug>`; only the NPI is read (see app/slug.ts for why
  // the NPI leads). A segment that isn't that shape — including the old
  // `<slug>--<npi>` order — is not a provider URL, so 404 before calling upstream.
  const npi = parseProviderNpi(params['id'])

  if (npi == null) {
    throw data('Not Found', { status: 404 })
  }

  // VIT-17749: one call. The providers API resolves the record across NPPES, the
  // curated `providers` row and the Ribbon cache — cross-source precedence lives
  // there (`core/provider-detail.ts`) because a loader can only reach HTTP
  // endpoints, while the service can read every source directly.
  //
  // Bounded exponential backoff, converting a persistent rejection into a value
  // rather than a throw, so a network failure (DNS, socket, TLS) is reported with
  // the same enrichment as a bad status instead of escaping as an unhandled 500.
  const result = await withRetries(
    () => fromPromise(providersClient.getProviderDetail({ params: { npi } })),
    exponential().map(jitter).take(MAX_RETRIES),
  )

  if (result.isErr) {
    // The signed fetch never produced an HTTP response, so there is no status to
    // report — only the rejection.
    const message = 'Provider source unavailable while retrieving a provider'
    logger.error({ npi }, message)
    throw new Error(message, { cause: result.error })
  }

  const response = result.value

  // 404 is expected, not an error — the endpoint 404s only when no source knows
  // the NPI at all. Don't log it.
  if (response.status === 404) {
    throw data('Not Found', { status: 404 })
  }

  if (response.status !== 200) {
    // 400/502 are the contract's declared error statuses (502 = every source
    // failed upstream); anything else is outside the contract — same enrichment
    // either way, distinct messages so the two stay separable in Datadog.
    const message =
      response.status === 400 || response.status === 502
        ? 'Handled error response while retrieving a provider'
        : 'Handled unexpected response status while retrieving a provider'
    // `status` and `message` are Datadog reserved attributes (remapped to log
    // severity / the displayed message), so the HTTP code and the upstream API
    // error go under `responseStatus` / `providerMessage` to survive ingestion.
    logger.error(
      {
        responseStatus: response.status,
        providerMessage: extractErrorMessage(response.status, response.body),
        npi,
      },
      message,
    )
    // `handleError` (entry.server.tsx) serializes `cause` via pino's err
    // serializer, so the upstream detail also reaches Datadog on the
    // unhandled-error log entry this throw produces.
    throw new Error(message, {
      cause: { responseStatus: response.status, body: response.body },
    })
  }

  const provider = response.body

  // A 200 can still be degraded: the endpoint answers off whichever sources
  // replied and reports the rest on `sources`. `miss` is normal (that source
  // simply doesn't know the NPI); `error` means an upstream failed and the page
  // is thinner than it should be, which is worth seeing in Datadog.
  const degraded = Object.entries(provider.sources).filter(([, state]) => state === 'error')
  if (degraded.length > 0) {
    logger.warn(
      { npi, providerSources: degraded.map(([source]) => source).join(',') },
      'Degraded provider read — endpoint assembled from the remaining sources',
    )
  }

  return data(
    { provider },
    { headers: { 'Cache-Control': degraded.length > 0 ? DEGRADED_CACHE_CONTROL : CACHE_CONTROL } },
  )
}

export const meta = ({ loaderData }: Route.MetaArgs): Route.MetaDescriptors => {
  // `loaderData` is undefined when the loader threw (404/error) — fall back to
  // generic wording so the tab still reads sensibly on the error boundary. The
  // endpoint guarantees a non-empty name (falls back to the NPI).
  const provider = loaderData?.provider
  const name = provider?.name
  return [
    { title: name != null ? `${name} | Vital Care Finder` : 'Provider | Vital Care Finder' },
    {
      name: 'description',
      content:
        name != null
          ? `Specialty, credentials, ratings and clinic contact details for ${name}, on Vital Care Finder.`
          : 'Specialty, credentials, ratings and clinic contact details for care providers on Vital Care Finder.',
    },
    // The slug is decorative and any spelling of it is served rather than redirected
    // (VIT-17797), so every variant would otherwise compete as duplicate content.
    // Point them all at the canonical spelling. Built from the NPI the endpoint
    // resolved rather than the one parsed out of the URL, and path-only so the
    // search params the card link forwards drop out too.
    ...(provider != null
      ? [
          {
            tagName: 'link',
            rel: 'canonical',
            href: href('/us/provider/:id', { id: formatProviderSlug(provider.npi, provider.name) }),
          } as const,
        ]
      : []),
  ]
}

const Provider = () => {
  const { provider } = useLoaderData<typeof loader>()
  const navigate = useNavigate()

  const {
    name,
    degreeSuffix,
    specialty,
    headshotUrl,
    organizationName,
    facilityName,
    address,
    phone,
    isPcp,
    ratingsAvg,
    ratingsCount,
    efficiencyIndex,
    focusTags,
    educations,
    certifications,
  } = provider

  // `formatPhoneNumber` only returns null for null/empty input, which `phone`
  // already excludes — the `?? phone` keeps the type honest.
  const displayPhone = phone != null ? (formatPhoneNumber(phone, false) ?? phone) : null

  // The endpoint returns the address structured so it owns resolution and we own
  // formatting. "New York, NY 10019" — the comma only earns its place when a city
  // is present.
  const statePostal = [address?.state, address?.postalCode].filter(isNonEmpty).join(' ')
  const cityStatePostal = [address?.city, statePostal === '' ? null : statePostal].filter(isNonEmpty).join(', ')
  const addressLine = [address?.line1, cityStatePostal].filter(isNonEmpty).join(', ')

  // The sidebar card is worth rendering only if it would carry something. Prefer
  // the location's own facility name over the provider's organisation name — the
  // sidebar describes a place, and the two are different things.
  const sidebarFacility = facilityName ?? organizationName
  const hasContactSummary = sidebarFacility || addressLine || displayPhone

  // "Austin, TX" — the city and state of the location this page is showing, so
  // the heading and the sidebar always name the same place. Null when the
  // location carries neither, rather than falling back to anything invented.
  // @TODO: decide where or if this is added back - not in any designs
  // const cityState = [address?.city, address?.state].filter(isNonEmpty).join(', ') || null

  // The hero credential badge shows the first named certification; the full list
  // still renders in the body section below.
  const primaryCertification = certifications[0] ?? null

  return (
    <main className="grow-1">
      <div className="border-b border-secondary-100 pb-4 xs:pb-10 pt-2 px-3 sm:px-4">
        <div className="mx-auto w-full max-w-7xl">
          <div className="flex flex-wrap items-center gap-4 xs:gap-6 mb-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn btn-primary btn-outline"
              data-testid="provider-back"
            >
              ← Results
            </button>
            {specialty != null && (
              <span
                className="rounded-full bg-purple-50 px-4 py-1.5 text-sm border border-transparent font-bold text-purple-700 uppercase tracking-wide"
                data-testid="provider-specialty"
              >
                {specialty}
              </span>
            )}
            {isPcp === true && (
              <span className="rounded-full bg-secondary-100 px-4 py-1.5 text-sm border border-transparent font-bold text-secondary-700 uppercase tracking-wide">
                Primary care
              </span>
            )}
          </div>

          <section className="grid grid-cols-1 gap-12">
            <div className="space-y-4 xs:space-y-6">
              <div className="flex items-stretch gap-4">
                <Avatar name={name} url={headshotUrl} className="size-27" />
                <div className="flex flex-col justify-between">
                  <h1
                    className="font-display text-2xl leading-tight font-semibold text-secondary-800 sm:text-4xl"
                    data-testid="provider-name"
                  >
                    {name}
                    {degreeSuffix != null && (
                      <span className="block text-lg font-sans font-normal text-secondary-600">{degreeSuffix}</span>
                    )}
                  </h1>
                  {(typeof ratingsAvg === 'number' || efficiencyIndex != null) && (
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                      {typeof ratingsAvg === 'number' && (
                        <div className="flex items-center gap-2" data-testid="provider-rating">
                          <Star className="size-5 stroke-none fill-primary-400" />
                          <span className="text-sm font-semibold text-secondary-800">{ratingsAvg.toFixed(1)}</span>
                          {ratingsCount != null && (
                            <span className="text-sm text-secondary-500">({ratingsCount} reviews)</span>
                          )}
                        </div>
                      )}
                      {efficiencyIndex != null && (
                        <div className="flex items-center gap-1.5" data-testid="provider-cost">
                          <span className="text-sm font-semibold text-secondary-800">Cost:</span>
                          <DollarRating
                            efficiencyIndex={efficiencyIndex}
                            className="text-sm font-semibold leading-none"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {hasContactSummary && (
                <div className="border-t pt-4 xs:pt-6">
                  <div className="bg-secondary-50 rounded-2xl p-4 space-y-4">
                    {hasContactSummary && (
                      <div className="flex items-center gap-3" data-testid="provider-contact-summary">
                        <div className="size-10 bg-primary-50 shrink-0 rounded-full grid place-items-center">
                          <Hospital className="size-5 text-primary-500" />
                        </div>
                        <div className="flex-1">
                          {sidebarFacility != null && (
                            <h3 className="text-secondary-900 text-xl leading-tight">{sidebarFacility}</h3>
                          )}
                          {addressLine !== '' && <p className="text-sm text-primary-500">{addressLine}</p>}
                        </div>
                      </div>
                    )}
                    {displayPhone != null && (
                      <a
                        href={formatTelHref(displayPhone)}
                        className="btn btn-primary btn-lg w-full sm:w-auto break-words"
                        data-testid="provider-phone"
                      >
                        <Phone className="size-5 shrink-0 text-primary-600 stroke-0 fill-white" />
                        {displayPhone}
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Maps will appear here - need to add grid-cols-[15fr_9fr] to the parent section and add the maps component here */}
          </section>
        </div>
      </div>

      <div className="px-3 sm:px-4">
        <div className="mx-auto w-full max-w-7xl py-4 sm:py-8 " data-testid="provider-page">
          {/* Body: main column + sidebar. Single column on mobile, 3-up from lg. */}
          <div className="grid grid-cols-1 gap-8  lg:grid-cols-[15fr_9fr]">
            <div className="space-y-6 sm:space-y-10 sm:*:pb-10 sm:divide-y sm:divide-secondary-100">
              {educations.length > 0 && (
                <section className="space-y-4">
                  <h2 className="text-secondary-700">Education & Credentials</h2>
                  <ul className="space-y-3 pl-8">
                    {educations.map((edu, index) => {
                      const year = edu.year != null ? String(edu.year) : null
                      const detail = [edu.type, year].filter(isNonEmpty).join(' · ')
                      return (
                        <li
                          // Education entries have no stable id; index within the
                          // loader-provided array is the only key available.
                          // biome-ignore lint/suspicious/noArrayIndexKey: no stable id on Ribbon education records
                          key={index}
                          className="relative before:block before:content-[''] before:size-4 before:bg-primary-300 before:rounded-full before:absolute before:-left-8 before:top-1/2 before:-translate-y-1/2"
                        >
                          <div className="font-semibold text-secondary-800 uppercase text-sm sm:text-base">
                            {edu.name}
                          </div>
                          {detail !== '' && <div className="text-sm text-secondary-700">{detail}</div>}
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )}

              {certifications.length > 0 && (
                <section className="space-y-4">
                  <h2 className="text-secondary-700">Board certifications</h2>
                  <ul className="divide-y divide-secondary-100">
                    {certifications.map((cert, index) => {
                      const detail = [cert.board, cert.startYear != null ? `Certified ${cert.startYear}` : null]
                        .filter(isNonEmpty)
                        .join(' · ')
                      return (
                        <li
                          // biome-ignore lint/suspicious/noArrayIndexKey: no stable id on Ribbon certification records
                          key={index}
                          className="flex flex-col gap-0.5 py-3 first:pt-0 last:pb-0"
                        >
                          <span className="font-medium text-secondary-800">{cert.name}</span>
                          {detail !== '' && <span className="text-sm text-secondary-700">{detail}</span>}
                        </li>
                      )
                    })}
                  </ul>
                </section>
              )}
            </div>

            {/* At-a-glance sidebar: board certification summary + specialized
                treatments, mirroring the redesign's right-column pattern. The
                facility card now lives under the hero instead of here. */}
            {(primaryCertification != null || focusTags.length > 0) && (
              <aside className="row-start-1 lg:row-start-auto space-y-6">
                {/* Board certification from the payload. Admitting privileges used to
                    sit alongside it as a hardcoded hospital — no source carries that
                    field, so the block is gone rather than invented. */}
                {primaryCertification != null && (
                  <section className="flex gap-2" data-testid="provider-board-certification-summary">
                    <div className="grid place-items-center size-10 bg-primary-50 rounded-full shrink-0">
                      <Shield className="size-5 text-primary-500 rounded-full" />
                    </div>
                    <div>
                      <div className="text-secondary-700 text-sm uppercase tracking-wider font-bold">
                        Board Certified
                      </div>
                      <p className="text-sm text-secondary-700" data-testid="provider-board-certification">
                        {primaryCertification.board ?? primaryCertification.name}
                      </p>
                    </div>
                  </section>
                )}

                {focusTags.length > 0 && (
                  <section className="space-y-4">
                    <h2 className="text-secondary-700">Specialized Treatments</h2>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:grid-cols-1">
                      {focusTags.map((tag) => (
                        <div
                          key={tag}
                          className="rounded-2xl bg-secondary-50 border font-medium border-secondary-100 p-4 text-sm text-secondary-700"
                        >
                          {tag}
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </aside>
            )}
          </div>
        </div>
      </div>
    </main>
  )
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // A thrown 404 Response means the path segment wasn't the `npi-<npi>-<slug>`
  // shape, or both provider sources reported not-found — the provider genuinely
  // isn't there,
  // so retrying won't help. Any other throw is a degraded/failed read of both
  // sources (bad status or network) and may clear on a retry. The upstream
  // detail stays out of the UI — it's logged for Datadog, not for visitors.
  const isNotFound = isRouteErrorResponse(error) && error.status === 404

  return isNotFound ? (
    <StatusScreen
      variant="not-found"
      data-testid="provider-error"
      title="We couldn't find that provider"
      description="This doctor may have been removed, or the link may be out of date. Try searching again to find another provider."
    >
      <BackHomeLink />
    </StatusScreen>
  ) : (
    <StatusScreen
      variant="error"
      data-testid="provider-error"
      title="Something went wrong"
      description="We couldn't load this provider just now — it's on us, not you. Please try again in a moment."
    >
      <TryAgainButton />
      <BackHomeLink />
    </StatusScreen>
  )
}

export default Provider
